import {
  EdgelessFrameManager,
  EdgelessFrameManagerIdentifier,
} from '@blocksuite/affine-block-frame';
import type { FrameSnapshotProps } from '@blocksuite/affine-model';
import {
  encodeClipboardBlobs,
  stampDatabaseClipboardSnapshot,
} from '@blocksuite/affine-shared/adapters';
import { Bound, getBoundWithRotation } from '@blocksuite/global/gfx';
import type { BlockStdScope } from '@blocksuite/std';
import { type GfxModel, type SerializedElement } from '@blocksuite/std/gfx';
import { type BlockSnapshot, BlockSnapshotSchema } from '@blocksuite/store';
import {
  FILE_INDEX_MANAGED_BY,
  generateFractionalIndexingKeyBetween,
} from '@streamlain/database-identity';
import DOMPurify from 'dompurify';

import { serializeElement } from '../utils/clone-utils';
import { isAttachmentBlock, isImageBlock } from '../utils/query';

type FrameSnapshot = BlockSnapshot & {
  props: FrameSnapshotProps;
};

function isFileIndexSystemProps(props: Record<string, unknown>): boolean {
  return (
    props.managedBy === FILE_INDEX_MANAGED_BY ||
    (props.systemManaged === true && props.docScoped === true)
  );
}

function isFileIndexSystemModel(model: GfxModel): boolean {
  const props = (model as { props?: Record<string, unknown> }).props;
  return !!props && isFileIndexSystemProps(props);
}

function isFileIndexSystemSnapshot(snapshot: BlockSnapshot): boolean {
  return isFileIndexSystemProps(snapshot.props as Record<string, unknown>);
}

export function stripFileIndexSystemClipboardData(
  raw: (SerializedElement | BlockSnapshot)[]
): (SerializedElement | BlockSnapshot)[] {
  // File Index artifacts are regenerated per doc. Clipboard/template/import
  // payloads keep user file blocks and drop the system DB plus nested internals.
  const stripped: (SerializedElement | BlockSnapshot)[] = [];

  for (const data of raw) {
    const { data: blockSnapshot } = BlockSnapshotSchema.safeParse(data);
    if (!blockSnapshot) {
      stripped.push(data as SerializedElement);
      continue;
    }
    if (isFileIndexSystemSnapshot(blockSnapshot)) continue;

    stripped.push({
      ...blockSnapshot,
      children: stripFileIndexSystemClipboardData(
        blockSnapshot.children
      ) as BlockSnapshot[],
    });
  }

  return stripped;
}

export async function createNewPresentationIndexes(
  raw: (SerializedElement | BlockSnapshot)[],
  std: BlockStdScope
) {
  const frames = raw
    .filter((block): block is FrameSnapshot => {
      const { data } = BlockSnapshotSchema.safeParse(block);
      return data?.flavour === 'affine:frame';
    })
    .sort((a, b) =>
      EdgelessFrameManager.framePresentationComparator(a.props, b.props)
    );

  const frameMgr = std.get(EdgelessFrameManagerIdentifier);
  if (frames.length === 0) return new Map<string, string>();
  await frameMgr.loadFrames();
  let before = frameMgr.generatePresentationIndex();
  const result = new Map<string, string>();
  frames.forEach(frame => {
    result.set(frame.id, before);
    before = generateFractionalIndexingKeyBetween(before, null);
  });

  return result;
}

export async function prepareClipboardData(
  selectedAll: GfxModel[],
  std: BlockStdScope,
  cutToken?: string
) {
  const job = std.store.getTransformer();
  const selected = await Promise.all(
    selectedAll.map(async selected => {
      if (isFileIndexSystemModel(selected)) {
        return;
      }
      const data = serializeElement(selected, selectedAll, job);
      if (!data) {
        return;
      }
      const block = BlockSnapshotSchema.safeParse(data);
      if (block.success) {
        stampDatabaseClipboardSnapshot(block.data, std.store, cutToken);
        Object.assign(data, block.data);
      }
      if (isAttachmentBlock(selected) || isImageBlock(selected)) {
        await job.assetsManager.readFromBlob(data.props.sourceId as string);
      }
      return data;
    })
  );
  const blobs = await encodeClipboardBlobs(job.assetsManager.getAssets());
  return {
    snapshot: stripFileIndexSystemClipboardData(selected.filter(d => !!d)),
    blobs,
  };
}

export function isPureFileInClipboard(clipboardData: DataTransfer) {
  const types = clipboardData.types;
  const allowedTypes = new Set([
    'Files',
    'text/plain',
    'text/html',
    'application/x-moz-file',
  ]);

  return types.includes('Files') && types.every(type => allowedTypes.has(type));
}

export function tryGetSvgFromClipboard(clipboardData: DataTransfer) {
  try {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(
      clipboardData.getData('text/plain'),
      'image/svg+xml'
    );
    const svg = svgDoc.documentElement;

    if (svg.tagName !== 'svg' || !svg.hasAttribute('xmlns')) {
      return null;
    }
    const svgContent = DOMPurify.sanitize(svgDoc.documentElement, {
      USE_PROFILES: { svg: true },
    });
    const blob = new Blob([svgContent], { type: 'image/svg+xml' });
    const file = new File([blob], 'pasted-image.svg', {
      type: 'image/svg+xml',
    });
    return file;
  } catch {
    return null;
  }
}

export function edgelessElementsBoundFromRawData(
  elementsRawData: (SerializedElement | BlockSnapshot)[]
) {
  if (elementsRawData.length === 0) return new Bound();

  let prev: Bound | null = null;

  for (const data of elementsRawData) {
    const { data: blockSnapshot } = BlockSnapshotSchema.safeParse(data);
    const bound = blockSnapshot
      ? getBoundFromGfxBlockSnapshot(blockSnapshot)
      : getBoundFromSerializedElement(data as SerializedElement);

    if (!bound) continue;
    if (!prev) prev = bound;
    else prev = prev.unite(bound);
  }

  return prev ?? new Bound();
}

function getBoundFromSerializedElement(element: SerializedElement) {
  return Bound.from(
    getBoundWithRotation({
      ...Bound.deserialize(element.xywh),
      rotate: typeof element.rotate === 'number' ? element.rotate : 0,
    })
  );
}

function getBoundFromGfxBlockSnapshot(snapshot: BlockSnapshot) {
  if (typeof snapshot.props.xywh !== 'string') return null;
  return Bound.deserialize(snapshot.props.xywh);
}
